import { useMemo, useState } from 'react';
import PropTypes from 'prop-types';

const columns = [
  { key: 'timestamp', label: 'Timestamp' },
  { key: 'engine', label: 'Engine' },
  { key: 'coin', label: 'Coin' },
  { key: 'side', label: 'Side' },
  { key: 'qty', label: 'Qty' },
  { key: 'price', label: 'Price' },
  { key: 'pnl', label: 'PnL' },
  { key: 'pnl_pct', label: 'PnL %' },
  { key: 'duration', label: 'Duration (s)' },
  { key: 'whale_influence', label: 'Whale?' },
  { key: 'confidence', label: 'Confidence' },
  { key: 'reason_code', label: 'Reason' },
];

function formatValue(value) {
  if (value === null || value === undefined || value === '') return '—';
  if (typeof value === 'number' && !Number.isNaN(value)) return value.toFixed(2);
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  return String(value);
}

export default function StrategyTradeHistory({ history = [] }) {
  const [sortKey, setSortKey] = useState('timestamp');
  const [sortDir, setSortDir] = useState('desc');
  const [engineFilter, setEngineFilter] = useState('all');
  const [coinFilter, setCoinFilter] = useState('all');

  const filtered = useMemo(() => {
    return history
      .filter((row) => (engineFilter === 'all' ? true : row.engine?.toLowerCase() === engineFilter))
      .filter((row) => (coinFilter === 'all' ? true : row.coin?.toUpperCase() === coinFilter));
  }, [history, engineFilter, coinFilter]);

  const sorted = useMemo(() => {
    const next = [...filtered];
    next.sort((a, b) => {
      const first = a[sortKey] ?? '';
      const second = b[sortKey] ?? '';
      if (first === second) return 0;
      const direction = sortDir === 'asc' ? 1 : -1;
      return first > second ? direction : -direction;
    });
    return next;
  }, [filtered, sortDir, sortKey]);

  const engines = Array.from(new Set(history.map((row) => (row.engine || '').toLowerCase()).filter(Boolean)));
  const coins = Array.from(new Set(history.map((row) => (row.coin || '').toUpperCase()).filter(Boolean)));

  const handleSort = (key) => {
    if (sortKey === key) {
      setSortDir((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('desc');
    }
  };

  return (
    <div className="rounded-2xl border border-gray-800 bg-gray-900/70 p-4" data-testid="trade-history">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
        <div>
          <p className="text-[0.65rem] uppercase tracking-wide text-gray-500">Execution</p>
          <h3 className="text-lg font-semibold text-white">Trade History</h3>
        </div>
        <div className="flex gap-2 text-xs text-gray-200">
          <select
            value={engineFilter}
            onChange={(e) => setEngineFilter(e.target.value)}
            className="rounded border border-gray-800 bg-gray-900 px-2 py-1"
          >
            <option value="all">All Engines</option>
            {engines.map((eng) => (
              <option key={eng} value={eng}>
                {eng.toUpperCase()}
              </option>
            ))}
          </select>
          <select
            value={coinFilter}
            onChange={(e) => setCoinFilter(e.target.value)}
            className="rounded border border-gray-800 bg-gray-900 px-2 py-1"
          >
            <option value="all">All Coins</option>
            {coins.map((coin) => (
              <option key={coin} value={coin}>
                {coin}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="overflow-auto rounded-xl border border-gray-800">
        <table className="min-w-full text-xs text-left text-gray-300">
          <thead className="bg-gray-800/60 text-gray-400">
            <tr>
              {columns.map((col) => (
                <th
                  key={col.key}
                  className="px-3 py-2 cursor-pointer"
                  onClick={() => handleSort(col.key)}
                >
                  <span className="inline-flex items-center gap-1">
                    {col.label}
                    {sortKey === col.key ? <span>{sortDir === 'asc' ? '▲' : '▼'}</span> : null}
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sorted.length === 0 ? (
              <tr>
                <td className="px-3 py-3 text-gray-500" colSpan={columns.length}>
                  No trades recorded yet.
                </td>
              </tr>
            ) : (
              sorted.map((row, idx) => (
                <tr key={`${row.timestamp}-${idx}`} className="border-t border-gray-800 hover:bg-gray-800/40">
                  {columns.map((col) => (
                    <td key={col.key} className="px-3 py-2 whitespace-nowrap">
                      {formatValue(row[col.key])}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

StrategyTradeHistory.propTypes = {
  history: PropTypes.arrayOf(PropTypes.object),
};
